import { Box, Typography } from '@mui/material';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { Pricebook, PricebookBundle, PaymentTypeEnum } from '../../../models/PricebookData';
import { PaidByEnum } from '../../../models/LicenseData';
import { Svgs } from '../../../assets/svg';
import { isFreemiumPricebook } from '../../../helpers/pricebookUtils';
import BundledTag from '../../../components/BundledTag';

interface PricebookCardTagsProps {
  pricebook: Pricebook;
  addOnPricebook?: PricebookBundle;
}

interface TagConfig {
  label: string;
  tooltip: string;
  icon?: React.ReactElement;
}

const getPaymentTypeTooltip = (pricebook: Pricebook): string => {
  const isOrgPays = pricebook.paidBy === PaidByEnum.Org;
  if (pricebook.paymentType === PaymentTypeEnum.Prepaid) {
    return isOrgPays
      ? 'Organization pays up front for any licenses, so sign-ups are limited to the number of licenses purchased.'
      : 'Member pays up front for their license.';
  }
  return 'Organization is billed at the end of each month for any active licenses.';
};

const getPaidByTooltip = (pricebook: Pricebook): string => {
  return pricebook.paidBy === PaidByEnum.Org
    ? "Organization pays for the member's license."
    : 'Member pays for their own license.';
};

const PricebookCardTags: React.FC<PricebookCardTagsProps> = ({ pricebook, addOnPricebook }) => {
  const isFreemium = isFreemiumPricebook(pricebook);

  const tags: TagConfig[] = [];

  if (pricebook.paymentType === PaymentTypeEnum.Prepaid) {
    tags.push({
      label: 'Pre-paid',
      tooltip: getPaymentTypeTooltip(pricebook),
      icon: <Svgs.IconPaymentDefault />,
    });
  } else if (pricebook.paymentType === PaymentTypeEnum.Metered) {
    tags.push({
      label: 'Metered',
      tooltip: getPaymentTypeTooltip(pricebook),
      icon: <Svgs.DisplayMediumCustomSpeedometer />,
    });
  }

  if (pricebook.paidBy === PaidByEnum.User) {
    tags.push({
      label: 'Member pays',
      tooltip: getPaidByTooltip(pricebook),
      icon: <Svgs.IconMemberPaysSmall />,
    });
  } else if (pricebook.paidBy === PaidByEnum.Org) {
    tags.push({
      label: 'Org pays',
      tooltip: getPaidByTooltip(pricebook),
      icon: <Svgs.IconBusinessSmall />,
    });
  }

  if (!tags.length && !isFreemium) return null;

  return (
    <Box display='flex' gap='5px' flexWrap='wrap'>
      {tags.map(tag => (
        <Tippy
          key={tag.label}
          content={
            <Typography variant='p16Light' color='secondary.dark'>
              {tag.tooltip}
            </Typography>
          }
          placement='top'
          arrow
          theme='tippy-rounded'
          trigger='mouseenter focus'>
          <Box className='pricebook-tag' role='button' tabIndex={0}>
            {tag.icon}
            <Typography variant='p12'>{tag.label}</Typography>
          </Box>
        </Tippy>
      ))}
      {isFreemium && <BundledTag addOnPricebook={addOnPricebook} />}
    </Box>
  );
};

export default PricebookCardTags;
